import type { ClaimLinkResponse } from "@solomon-apps/shared";
import { ContentBlock } from "@solomon-apps/shared/src/components/console";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import { Card, CardContent, Copy } from "@solomon-labs/design/components/ui";
import { motion, useReducedMotion } from "motion/react";
import React from "react";

import { LabeledInput } from "@/pages/shared/LabeledField";
import { OUTLINE_GROUP_CLASS } from "@/pages/shared/lightbarField";

/** How the invitation went out, stated beside the card title. */
const DELIVERY_LABEL: Record<ClaimLinkResponse["delivery"], string> = {
    email_and_url: "Email Sent",
    // Email delivery was unavailable: the link is here to send by hand.
    url: "Link Only"
};

/**
 * The member page's right column straight after Add Member creates an
 * invitation: the new claim link, read-only, to copy. Same shell as
 * `ResetPasswordCard` (a sunken, outlined card holding an outline field),
 * so the two links a member page can hand out read as one construction.
 *
 * The link exists only in the invite response, so the card shows only on
 * the page the invite navigated to; the delivery state sits beside the title.
 */
export function InvitationLinkCard({
    invitation
}: {
    invitation: ClaimLinkResponse;
}): React.ReactElement {
    const reducedMotion = useReducedMotion() ?? false;
    return (
        // Arrives with a fade (opacity only, 200ms ease-out-quad) after the
        // page enter, skipped under reduced motion.
        <motion.div
            initial={reducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, ease: easings.outQuad }}
        >
            <Card surface="sunken" border="line">
                <CardContent className="pt-6">
                    <ContentBlock
                        title="Invitation Link"
                        actions={
                            <Text
                                variant="small"
                                render={<span />}
                                className="text-foreground-muted"
                            >
                                {DELIVERY_LABEL[invitation.delivery]}
                            </Text>
                        }
                        className="gap-1 md:gap-2"
                    >
                        <div className="flex flex-col gap-3 py-4">
                            <Copy
                                content={invitation.claimUrl}
                                copyLabel="Copy invitation link"
                                copiedLabel="Copied"
                                className="flex w-full"
                            >
                                <LabeledInput
                                    label="Invite Link"
                                    groupClassName={OUTLINE_GROUP_CLASS}
                                    type="text"
                                    value={invitation.claimUrl}
                                    readOnly
                                    aria-label="Invite Link"
                                    onFocus={(e) => e.currentTarget.select()}
                                />
                            </Copy>
                        </div>
                    </ContentBlock>
                </CardContent>
            </Card>
        </motion.div>
    );
}
